import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import DraggableContainer from '@components/system/DraggableContainer/DraggableContainer';
import {Card, CardContent, CardHeader} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {useSnackbar} from 'notistack';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import {Permission} from '@shared/services/PermissionService';
import {
  AppPermissions,
  PatientsTabKeys,
  PriorAuthConstants,
  TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER
} from '@shared/constants/Mapping';
import {
  _patientDrawerDetails,
  _setActiveTab
} from '@shared/store/actions/PatientDrawerAction';
import {CommunicationService} from 'src/service/CommunicationService';
import Storage from 'src/utils/Storage';
import {State} from '@shared/store/reducers/Index';
import {DraggableTicketingSystemTicketTimerCardStyles} from './DraggableTicketingSystemTicketTimerCardStyles';
import TicketingSystemTimer from '../TicketingSystemTimer';
import {
  getTicketDetails,
  getWorkflowDetails
} from '@shared/services/WorkflowServices';
import {
  IClaimsTicketDetails,
  IPriorAuthTicketDetails,
  IWorkFlow,
  IWorkFlowStatus
} from '@shared/services/service-mappers/WorkflowMapper';
// import {fetchAllAppointments} from '@shared/services/AppointmentsServices';
// import {IAppointment} from '@shared/services/service-mappers/AppointmentMapper';
// import {appointmentsDateTimeFormat} from '@shared/utils/DateTimeUtils';
import {Subscription} from 'rxjs';
import {IAllPossibleTicketModules} from '../../hooks/useTicketingSystemPermission';
import usePriorAuthPermission from '../../../PriorAuthorization/hooks/usePriorAuthPermission';
import useClaimsTicketsPermissions from '../../../ClaimsTickets/hooks/useClaimsTicketsPermissions';
import useReferralsTicketsPermissions from '../../../ReferralsTickets/hooks/useReferralsTicketsPermission';

interface TicketPanelData {
  clinicGroupId: number;
  clinicId: number;
  patientId: number;
  ticketId: number;
  logId: number;
  patientName?: string;
  patientAccountNumber?: string;
  guarantorId: number;
  module: string;
}

const DraggableTicketingSystemTicketTimerCard = ({
  clinicGroupId,
  initialTicketPanelData
}: {
  clinicGroupId: number;
  initialTicketPanelData?: TicketPanelData;
}): JSX.Element => {
  const classes = DraggableTicketingSystemTicketTimerCardStyles();
  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslation();
  const subscription = React.useRef<Subscription>();

  const [
    ticketPanelData,
    setTicketPanelData
  ] = useState<null | TicketPanelData>(initialTicketPanelData ?? null);

  const [ticketDetails, setTicketDetails] = useState({
    data: null as IPriorAuthTicketDetails | IClaimsTicketDetails | null,
    isLoading: false
  });

  // const [ticketData, setTicketData] = useState<{
  //   patientAppointmentsList: IAppointment[];
  // }>({
  //   patientAppointmentsList: []
  // });
  // const {patientAppointmentsList} = ticketData;

  const cardRef = useRef<any>();
  const dispatch = useDispatch();
  const userPermissions = useSelector(
    (state: State) => state.auth.userPermissions
  );

  const isPatientDetailsDrawerOpen = useSelector(
    (state: any) => state.patientDetailsDrawer.open
  );

  const hasClaimsTicketsPermission = useClaimsTicketsPermissions();
  const [claimsTicketsWorkflow, setClaimsTicketsWorkflow] = useState({
    isLoading: false,
    data: null as null | IWorkFlow
  });
  useEffect(() => {
    const fetchClaimsWorkflow = async () => {
      try {
        setClaimsTicketsWorkflow(p => ({
          ...p,
          isLoading: true
        }));
        const response = await getWorkflowDetails({
          clinicGroupId: clinicGroupId,
          params: {
            module:
              TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER['CLAIMS_TICKETS']
          }
        });
        if (response.data.workflow) {
          setClaimsTicketsWorkflow({
            isLoading: false,
            data: response.data.workflow
          });
        } else {
          setClaimsTicketsWorkflow({
            isLoading: false,
            data: null
          });
        }
      } catch (err: any) {
        enqueueSnackbar(err?.message, SnackBarConfig('e'));
        setClaimsTicketsWorkflow({
          isLoading: false,
          data: null
        });
      }
    };
    if (hasClaimsTicketsPermission && clinicGroupId) {
      fetchClaimsWorkflow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicGroupId]);

  const hasPriorAuthPermission = usePriorAuthPermission();
  const [priorAuthWorkflow, setPriorAuthWorkflow] = useState({
    isLoading: false,
    data: null as null | IWorkFlow
  });
  useEffect(() => {
    const fetchPriorAuthWorkflow = async () => {
      try {
        setPriorAuthWorkflow(p => ({
          ...p,
          isLoading: true
        }));

        const response = await getWorkflowDetails({
          clinicGroupId: clinicGroupId,
          params: {
            module:
              TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER[
                'PRIOR_AUTHORIZATION'
              ]
          }
        });
        if (response.data.workflow) {
          setPriorAuthWorkflow({
            isLoading: false,
            data: response.data.workflow
          });
        } else {
          setPriorAuthWorkflow({
            isLoading: false,
            data: null
          });
        }
      } catch (err: any) {
        enqueueSnackbar(err?.message, SnackBarConfig('e'));
        setPriorAuthWorkflow({
          isLoading: false,
          data: null
        });
      }
    };
    if (hasPriorAuthPermission && clinicGroupId) {
      fetchPriorAuthWorkflow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicGroupId, hasPriorAuthPermission]);

  const hasReferralsPermission = useReferralsTicketsPermissions();
  const [referralsAuthWorkflow, setReferralsAuthWorkflow] = useState({
    isLoading: false,
    data: null as null | IWorkFlow
  });
  useEffect(() => {
    const fetchReferralsWorkflow = async () => {
      try {
        setReferralsAuthWorkflow(p => ({
          ...p,
          isLoading: true
        }));

        const response = await getWorkflowDetails({
          clinicGroupId: clinicGroupId,
          params: {
            module:
              TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER[
                'REFERRALS_TICKETS'
              ]
          }
        });
        if (response.data.workflow) {
          setReferralsAuthWorkflow({
            isLoading: false,
            data: response.data.workflow
          });
        } else {
          setReferralsAuthWorkflow({
            isLoading: false,
            data: null
          });
        }
      } catch (err: any) {
        enqueueSnackbar(err?.message, SnackBarConfig('e'));
        setReferralsAuthWorkflow({
          isLoading: false,
          data: null
        });
      }
    };
    if (hasReferralsPermission && clinicGroupId) {
      fetchReferralsWorkflow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicGroupId, hasReferralsPermission]);

  const getWorkflowStatusLabel = (curStatusId?: IWorkFlowStatus['id']) => {
    let statusesList: IWorkFlowStatus[] = [];
    if (ticketPanelData?.module === 'PRIOR_AUTHORIZATION') {
      statusesList = priorAuthWorkflow?.data?.status ?? [];
    } else if (ticketPanelData?.module === 'CLAIMS_TICKETS') {
      statusesList = claimsTicketsWorkflow?.data?.status ?? [];
    } else if (ticketPanelData?.module === "REFERRALS_TICKETS") {
      statusesList = referralsAuthWorkflow?.data?.status ?? [];
    }
    if (curStatusId) {
      const curStatusObj = statusesList.find(s => s.id === curStatusId);
      if (!curStatusObj) return '';
      return curStatusObj.label;
    }
    return '-';
  };

  // const clinicList = useSelector((state: State) => state.clinics.list || []);

  // const getClinicTimeZone = (_clinicId: number): string => {
  //   let timezone = '';
  //   if (clinicList && Array.isArray(clinicList)) {
  //     const clinic = clinicList.find(c => c.id === _clinicId);
  //     if (clinic) {
  //       timezone = clinic?.zone;
  //     }
  //   }
  //   return timezone;
  // };

  const updateTicketPanelData = () => {
    const ticketTimerDataStr = Storage.getItem(
      PriorAuthConstants.STORAGE_PRIOR_AUTH_TIMER_DATA
    );
    if (ticketTimerDataStr) {
      const parsedTicketTimerData = JSON.parse(ticketTimerDataStr);
      if (parsedTicketTimerData && parsedTicketTimerData?.ticketPanelData) {
        setTicketPanelData(parsedTicketTimerData?.ticketPanelData);
      }
    }
  };

  useEffect(() => {
    updateTicketPanelData();
  }, []);

  useEffect(() => {
    const eventSubscription = CommunicationService.getEvent().subscribe(
      event => {
        if (event.action === 'TICKET_TIMER_PANEL_DATA_UPDATED') {
          // read from the local storage and update the local state
          updateTicketPanelData();
        }
      }
    );
    return () => eventSubscription.unsubscribe();
  }, []);

  const openPatientDrawer = () => {
    const modulePatientDrawerTabKey =
      PatientsTabKeys[ticketPanelData?.module as keyof typeof PatientsTabKeys];
    if (ticketPanelData && modulePatientDrawerTabKey) {
      if (isPatientDetailsDrawerOpen) {
        dispatch(_setActiveTab(modulePatientDrawerTabKey));
      } else if (
        ticketPanelData?.clinicGroupId &&
        ticketPanelData.clinicId &&
        ticketPanelData?.patientId
      ) {
        dispatch(
          _patientDrawerDetails(
            ticketPanelData?.clinicGroupId,
            ticketPanelData.clinicId,
            ticketPanelData?.patientId,
            modulePatientDrawerTabKey
          )
        );
      }
    }
  };

  const onSendMessageClick = async (): Promise<void> => {
    try {
      if (ticketPanelData) {
        if (isPatientDetailsDrawerOpen) {
          dispatch(_setActiveTab(PatientsTabKeys.MESSAGES));
        } else {
          dispatch(
            _patientDrawerDetails(
              ticketPanelData?.clinicGroupId,
              ticketPanelData.clinicId,
              ticketPanelData?.patientId,
              PatientsTabKeys.MESSAGES
            )
          );
        }
      }
    } catch (err: any) {
      enqueueSnackbar(err && err.message, SnackBarConfig('e'));
      // throw err;
    }
  };
  const fetchTicketDetails = async (ticketPanelParamData: any) => {
    if (
      !ticketPanelParamData &&
      ticketPanelParamData?.module &&
      TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER[
        ticketPanelParamData?.module as IAllPossibleTicketModules
      ]
    )
      return;
    try {
      setTicketDetails(prev => ({
        ...prev,
        isLoading: true
      }));
      const ticketDetailsRes = await getTicketDetails({
        clinicGroupId,
        clinicId: ticketPanelParamData?.clinicId,
        ticketId: ticketPanelParamData?.ticketId,
        params: {
          module:
            TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER[
              ticketPanelParamData?.module as IAllPossibleTicketModules
            ]
        }
      });
      if (ticketDetailsRes.data) {
        setTicketDetails({
          data: ticketDetailsRes.data,
          isLoading: false
        });
      }
    } catch (err: any) {
      enqueueSnackbar(err?.message, SnackBarConfig('e'));
      setTicketDetails(prev => ({
        ...prev,
        isLoading: false
      }));
    }
  };

  useEffect(() => {
    if (ticketPanelData?.ticketId) {
      fetchTicketDetails(ticketPanelData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketPanelData?.ticketId]);

  // NOTE: ** if the status gets updated in the ticket, then this useEffect
  // will execute to get the updated API response and fetches the lates ticket details **//
  useEffect(() => {
    // if there is an update available and no state passed
    subscription.current = CommunicationService.getEvent().subscribe(data => {
      if (data.action === 'TICKET_TIMER_PANEL_DATA_UPDATED') {
        fetchTicketDetails(ticketPanelData);
      }
    });
    return () => {
      subscription.current?.unsubscribe();
    };
    // eslint-disable-next-line
  }, []);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <React.Fragment>
      {ticketPanelData && (
        <DraggableContainer bounds="parent" handle="#draggableTicket">
          <Card className={classes.ticketFloatCard} ref={cardRef}>
            <CardHeader
              className={classes.ticketFloatCardHeader}
              title={
                <div
                  id="draggableTicket"
                  className={classes.ticketFloatCardHeaderWrapper}>
                  <DragIndicatorIcon className={classes.ticketPanelDragIcon} />
                  <p>{t('TICKETING_SYSTEM.TIMER.TICKET_PANEL.TITLE')}</p>
                </div>
              }
            />
            <CardContent className={classes.cardContent}>
              <div className={classes.ticketTimerWrapper}>
                <TicketingSystemTimer
                  patientId={ticketPanelData?.patientId}
                  ticketId={ticketPanelData?.ticketId}
                  clinicGroupId={ticketPanelData?.clinicGroupId}
                  clinicId={ticketPanelData?.clinicId}
                  module={ticketPanelData?.module as IAllPossibleTicketModules}
                  isFromFloatingTicketPanel={true}
                />
              </div>
              <div style={{display: 'grid', gap: '0.5rem'}}>
                <div className={classes.ticketPanelInfoWrapper}>
                  <Card>
                    <CardHeader
                      title={
                        <div className={classes.ticketPatientDetailsHeader}>
                          <p>{t('CCM.CCM_FLOAT_PANEL.PATIENT_DETAILS')}</p>
                          {Permission.hasIncomingSmsPermissions(
                            userPermissions,
                            AppPermissions.INCOMING_SMS.REPLY,
                            clinicGroupId
                          ) && (
                            <p
                              className={`link-text-style ${classes.link}`}
                              onClick={onSendMessageClick}>
                              {t('CCM.CCM_FLOAT_PANEL.SEND_MESSAGE')}
                            </p>
                          )}
                        </div>
                      }
                    />
                    <CardContent
                      className={classes.ticketPatientInfoCardContent}>
                      <div className={classes.ticketPatientInfoWrapper}>
                        <p>{t('CCM.CCM_FLOAT_PANEL.NAME')}</p>
                        <p>:</p>
                        <p
                          className={`link-text-style ${classes.link}`}
                          onClick={openPatientDrawer}>
                          {ticketPanelData?.patientName || '-'}
                        </p>
                        <p>{t('CCM.CCM_FLOAT_PANEL.ACCOUNT_NUMBER')}</p>
                        <p>:</p>
                        <p>{ticketPanelData?.patientAccountNumber || '-'}</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                <div className={classes.ticketPanelInfoWrapper}>
                  <Card>
                    <CardHeader
                      title={
                        <div className={classes.ticketPatientDetailsHeader}>
                          <p>
                            {t(
                              'TICKETING_SYSTEM.TIMER.TICKET_PANEL.TICKET_DETAILS.HEADER'
                            )}
                          </p>
                        </div>
                      }
                    />
                    <CardContent
                      className={classes.ticketPatientInfoCardContent}>
                      <div className={classes.ticketPatientInfoWrapper}>
                        <p>
                          {t(
                            'TICKETING_SYSTEM.TIMER.TICKET_PANEL.TICKET_DETAILS.TITLE'
                          )}
                        </p>
                        <p>:</p>
                        <p>{ticketDetails?.data?.title || '-'}</p>
                        <p>{t('TICKETING_SYSTEM.TICKET_DETAILS.STATUS')}</p>
                        <p>:</p>
                        <p>
                          {getWorkflowStatusLabel(
                            ticketDetails?.data?.workflow_status_id
                          )}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </CardContent>
          </Card>
        </DraggableContainer>
      )}
    </React.Fragment>
  );
};
export default DraggableTicketingSystemTicketTimerCard;
